import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'evermark-cart-v1';
const COUPON_KEY  = 'evermark-cart-coupon-v1';

// Coupon catalog (UI-only, no server validation).
const COUPONS = {
  EVERMARK10:   { code: 'EVERMARK10',   type: 'percent', value: 10, label: '10% off your bag' },
  WELCOME15:    { code: 'WELCOME15',    type: 'percent', value: 15, label: '15% off (first order)' },
  FREESHIP:     { code: 'FREESHIP',     type: 'freeship', label: 'Complimentary shipping' },
  MOUNTAIN20:   { code: 'MOUNTAIN20',   type: 'percent', value: 20, label: '20% off your bag' },
};

// Simple mock shipping / tax rules.
const SHIPPING_THRESHOLD = 150;
const SHIPPING_FLAT = 12;
const TAX_RATE = 0.08875; // NY, e-commerce baseline

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    if (typeof window === 'undefined') return [];
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [coupon, setCouponState] = useState(() => {
    if (typeof window === 'undefined') return null;
    try { return JSON.parse(localStorage.getItem(COUPON_KEY)); } catch { return null; }
  });

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(COUPON_KEY, JSON.stringify(coupon)); }, [coupon]);

  const add = useCallback((product, { size, color, quantity = 1 }) => {
    setItems((prev) => {
      const key = `${product.id}-${size}-${color?.name || ''}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      const unit = product.salePrice || product.price;
      return [
        ...prev,
        {
          key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          price: unit,
          originalPrice: product.price,
          image: product.images[0],
          size,
          color,
          quantity,
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const remove = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const updateQty = useCallback((key, quantity) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, quantity) } : i)));
  }, []);
  const clear = useCallback(() => { setItems([]); setCouponState(null); }, []);

  // Apply coupon by code; returns { ok, message }.
  const applyCoupon = useCallback((codeRaw) => {
    const code = (codeRaw || '').trim().toUpperCase();
    if (!code) return { ok: false, message: 'Enter a code.' };
    const match = COUPONS[code];
    if (!match) return { ok: false, message: 'This code isn\'t recognised.' };
    setCouponState(match);
    return { ok: true, message: `${match.label} applied.` };
  }, []);
  const removeCoupon = useCallback(() => setCouponState(null), []);

  // Derived totals.
  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    let couponDiscount = 0;
    let freeShipOverride = false;
    if (coupon) {
      if (coupon.type === 'percent') couponDiscount = subtotal * (coupon.value / 100);
      if (coupon.type === 'freeship') freeShipOverride = true;
    }
    const afterCoupon = Math.max(0, subtotal - couponDiscount);
    const qualifiesFreeShip = afterCoupon >= SHIPPING_THRESHOLD || freeShipOverride || items.length === 0;
    const shipping = qualifiesFreeShip ? 0 : SHIPPING_FLAT;
    const tax = afterCoupon * TAX_RATE;
    const total = afterCoupon + shipping + tax;
    return {
      subtotal: round(subtotal),
      couponDiscount: round(couponDiscount),
      shipping: round(shipping),
      tax: round(tax),
      total: round(total),
      qualifiesFreeShip,
      shippingThreshold: SHIPPING_THRESHOLD,
      taxRate: TAX_RATE,
      amountToFreeShip: round(Math.max(0, SHIPPING_THRESHOLD - afterCoupon)),
    };
  }, [items, coupon]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{
      items, add, remove, updateQty, clear,
      isOpen, setIsOpen,
      coupon, applyCoupon, removeCoupon,
      count,
      // Legacy field kept for backwards compat with older components.
      subtotal: totals.subtotal,
      totals,
      knownCoupons: Object.values(COUPONS),
    }}>
      {children}
    </CartContext.Provider>
  );
}

function round(n) { return Math.round(n * 100) / 100; }

export const useCart = () => useContext(CartContext);
